import React from "react";
import { Link, useLocation } from "react-router-dom";
import {
  Menu,
  ChevronRight,
  Home,
  Settings,
  CreditCard,
  LogOut,
  Sun,
  Moon,
  Keyboard,
} from "lucide-react";
import { useTheme } from "@/context/ThemeContext";
import { useCommands } from "@/context/CommandsContext";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/lib/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/lib/ui/avatar";
import type { BreadcrumbItem } from "@/types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface HeaderProps {
  title?: string;
  breadcrumbs?: BreadcrumbItem[];
  onMenuClick?: () => void;
  variant?: "user" | "admin";
  actions?: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Breadcrumb item component
// ---------------------------------------------------------------------------

interface BreadcrumbItemComponentProps {
  item: BreadcrumbItem;
  isLast: boolean;
}

const BreadcrumbItemComponent: React.FC<BreadcrumbItemComponentProps> = ({
  item,
  isLast,
}) => {
  const baseClass = "text-sm font-medium transition-colors";

  return (
    <li className="flex items-center gap-1">
      {isLast ? (
        <span className={cn(baseClass, "text-foreground")} aria-current="page">
          {item.label}
        </span>
      ) : (
        <>
          <Link
            to={item.href!}
            className={cn(
              baseClass,
              "text-muted-foreground hover:text-foreground",
            )}
          >
            {item.label}
          </Link>
          <ChevronRight
            className="w-4 h-4 text-border shrink-0"
            aria-hidden="true"
          />
        </>
      )}
    </li>
  );
};

// ---------------------------------------------------------------------------
// Theme toggle button
// ---------------------------------------------------------------------------

const ThemeToggle: React.FC = () => {
  const { theme, toggleTheme } = useTheme();
  const isDark = theme === "dark";

  return (
    <button
      type="button"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      onClick={toggleTheme}
      className={cn(
        "inline-flex items-center justify-center w-8 h-8 rounded-lg",
        "text-muted-foreground hover:text-foreground hover:bg-muted",
        "dark:text-muted-foreground dark:hover:text-muted-foreground/40 dark:hover:bg-slate-800",
        "transition-colors duration-150",
        "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
      )}
    >
      {isDark ? (
        <Sun className="w-4 h-4" aria-hidden="true" />
      ) : (
        <Moon className="w-4 h-4" aria-hidden="true" />
      )}
    </button>
  );
};

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

const Header: React.FC<HeaderProps> = ({
  breadcrumbs,
  onMenuClick,
  variant = "user",
  actions,
}) => {
  const { user, logout } = useAuth();
  const { openShortcutsHelp } = useCommands();
  const location = useLocation();

  const isAdmin = variant === "admin";

  const resolvedBreadcrumbs: BreadcrumbItem[] =
    breadcrumbs ??
    (() => {
      const segments = location.pathname.split("/").filter(Boolean);
      if (segments.length <= 1) return [];

      const crumbs: BreadcrumbItem[] = [
        {
          label: "Home",
          href: isAdmin ? "/admin/dashboard" : "/app/dashboard",
        },
      ];

      let accumulated = "";
      for (let i = 0; i < segments.length; i++) {
        accumulated += `/${segments[i]}`;
        const isLast = i === segments.length - 1;

        // Skip the section root ("app"/"admin") — the Home icon already
        // points there, so showing it again is just noise.
        if (i === 0 && (segments[i] === "app" || segments[i] === "admin")) {
          continue;
        }

        const label = /^\d+$/.test(segments[i])
          ? `#${segments[i]}`
          : segments[i]
              .replace(/-/g, " ")
              .replace(/\b\w/g, (c) => c.toUpperCase());

        crumbs.push({
          label,
          href: isLast ? undefined : accumulated,
        });
      }

      return crumbs;
    })();

  const hasBreadcrumbs = resolvedBreadcrumbs.length > 0;

  const initials = user
    ? `${user.firstName?.[0] ?? ""}${user.lastName?.[0] ?? ""}`.toUpperCase() ||
      user.email?.[0]?.toUpperCase() ||
      "U"
    : "U";

  const displayName =
    user?.firstName && user?.lastName
      ? `${user.firstName} ${user.lastName}`
      : (user?.email ?? "User");

  return (
    <header
      className={cn(
        "flex items-center gap-4 px-6 bg-background",
        "border-b border-border",
        hasBreadcrumbs ? "min-h-[64px] py-3" : "h-16",
        isAdmin && "border-t-2 border-t-red-500",
      )}
      role="banner"
    >
      {/* ── Mobile hamburger ──────────────────────────────────────────── */}
      <button
        type="button"
        aria-label="Open navigation menu"
        onClick={onMenuClick}
        className={cn(
          "lg:hidden inline-flex items-center justify-center w-8 h-8 rounded-lg",
          "text-muted-foreground hover:text-foreground hover:bg-accent",
          "transition-colors duration-150",
          "focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
          "shrink-0",
        )}
      >
        <Menu className="w-5 h-5" aria-hidden="true" />
      </button>

      {/* ── Breadcrumbs ───────────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {hasBreadcrumbs && resolvedBreadcrumbs.length > 1 && (
          <nav aria-label="Breadcrumb">
            <ol
              role="list"
              className="flex items-center flex-wrap gap-x-1 gap-y-0.5"
            >
              <li className="flex items-center gap-1.5">
                <Link
                  to={isAdmin ? "/admin/dashboard" : "/app/dashboard"}
                  aria-label="Home"
                  className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Home className="w-4 h-4" aria-hidden="true" />
                </Link>
                <ChevronRight
                  className="w-4 h-4 text-border shrink-0"
                  aria-hidden="true"
                />
              </li>

              {resolvedBreadcrumbs.slice(1).map((crumb, idx, arr) => (
                <BreadcrumbItemComponent
                  key={`${crumb.label}-${idx}`}
                  item={crumb}
                  isLast={idx === arr.length - 1}
                />
              ))}
            </ol>
          </nav>
        )}
      </div>

      {/* ── Right side: actions + theme toggle + avatar dropdown ─────── */}
      <div className="flex items-center gap-2 shrink-0">
        {actions && (
          <div className="hidden sm:flex items-center gap-2">{actions}</div>
        )}

        {!isAdmin && (
          <button
            type="button"
            onClick={openShortcutsHelp}
            aria-label="Keyboard shortcuts"
            title="Keyboard shortcuts (?)"
            className="hidden h-8 w-8 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring sm:inline-flex"
          >
            <Keyboard className="h-4 w-4" aria-hidden="true" />
          </button>
        )}

        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              aria-label="Open user menu"
              className={cn(
                "rounded-full focus:outline-none focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-1",
              )}
            >
              <Avatar className="w-8 h-8">
                <AvatarFallback
                  className={cn(
                    "text-xs font-semibold",
                    isAdmin
                      ? "bg-red-100 text-red-700 dark:text-red-300 dark:bg-red-900/30 dark:text-red-300"
                      : "bg-brand/10 text-brand",
                  )}
                >
                  {initials}
                </AvatarFallback>
              </Avatar>
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col gap-0.5">
                <span className="font-semibold text-foreground">
                  {displayName}
                </span>
                <span className="text-xs text-muted-foreground font-normal">
                  {user?.email}
                </span>
                {isAdmin && (
                  <span className="text-xs text-red-500 font-medium mt-0.5">
                    Admin Panel
                  </span>
                )}
              </div>
            </DropdownMenuLabel>

            <DropdownMenuSeparator />

            <DropdownMenuItem asChild>
              <Link
                to="/app/profile"
                className="flex items-center gap-2 cursor-pointer"
              >
                <Settings className="w-4 h-4" aria-hidden="true" />
                Profile &amp; Settings
              </Link>
            </DropdownMenuItem>

            {!isAdmin && (
              <DropdownMenuItem asChild>
                <Link
                  to="/app/billing"
                  className="flex items-center gap-2 cursor-pointer"
                >
                  <CreditCard className="w-4 h-4" aria-hidden="true" />
                  Billing
                </Link>
              </DropdownMenuItem>
            )}

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={logout}
              className="flex items-center gap-2 text-red-600 dark:text-red-300 focus:text-red-600 dark:text-red-300 focus:bg-red-50 dark:focus:bg-red-900/30 dark:bg-red-900/30 cursor-pointer"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
